import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Divider from "@/components/shared/Divider/Divider";
import { ISalesDetails } from "@/@types/Sells/get-details";
import { getOfferTypeLabel, userInitials } from "@/lib/utils";
import { ExternalLink, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type SaleOffer = ISalesDetails["offers"][number];

interface Props {
  sale:
    | Pick<ISalesDetails, "offers">
    | { offers: SaleOffer[] }
    | {
        offers: SaleOffer[];
        currency?: string | null;
        settlement_currency?: string | null;
        currencyConversion?: { to_currency?: string } | null;
      };
}

const SaleSlider = ({ sale }: Props) => {
  const { t } = useTranslation(["common", "sales", "signatures"]);

  const saleCurrencyFields = sale as {
    currencyConversion?: { to_currency?: string } | null;
    settlement_currency?: string | null;
    currency?: string | null;
  };
  const settlementCurrency =
    saleCurrencyFields.currencyConversion?.to_currency ||
    saleCurrencyFields.settlement_currency ||
    saleCurrencyFields.currency ||
    null;

  return (
    <TooltipProvider delayDuration={150}>
      <Swiper
        spaceBetween={16}
        pagination={{ clickable: true }}
        breakpoints={{
          320: { slidesPerView: 1 },
          768: { slidesPerView: 1.3 },
          1300: { slidesPerView: 2.1 },
        }}
        className="w-full [&_.swiper-wrapper]:items-start"
      >
        {sale.offers.map((offer, index) => {
          const isRecurring = offer.payment_type === "recurring";
          const intervalKey =
            offer.interval_label_key || offer.recurring_interval || null;
          const intervalLabel =
            isRecurring && intervalKey
              ? t(`common:paymentFrequency.${intervalKey}`, {
                  defaultValue: intervalKey,
                })
              : null;
          const hasSubscriptionLink = Boolean(offer.subscription_public_id);
          const isCurrent = Boolean(offer.is_current_subscription);
          const showCurrencyNote = Boolean(
            hasSubscriptionLink &&
              offer.subscription_currency &&
              settlementCurrency &&
              offer.subscription_currency.toUpperCase() !==
                settlementCurrency.toUpperCase(),
          );
          const currencyNote = showCurrencyNote
            ? t("signatures:details.catalog_currency_note", {
                currency: offer.subscription_currency,
                amount: offer.subscription_amount_formatted,
                to_currency: settlementCurrency,
              })
            : null;

          const showOfferName =
            Boolean(offer.name) &&
            offer.name.trim().toLowerCase() !==
              offer.product.name.trim().toLowerCase();

          const metaLine = [
            getOfferTypeLabel(offer.type),
            isRecurring
              ? intervalLabel || t("common:recurringPayment")
              : offer.payment_type === "single"
                ? t("common:singlePayment")
                : null,
            hasSubscriptionLink && offer.subscription_current_cycle != null
              ? `${t("signatures:details.cycle")} ${offer.subscription_current_cycle}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <SwiperSlide
              style={{ maxWidth: "284px", width: "100%", height: "auto" }}
              className="w-[284px] !h-auto"
              key={offer.offer_id || offer.id || index}
            >
              <Card className="xl:max-w-[284px] w-full p-4">
                <div className="flex items-start justify-between gap-3">
                  <Avatar className="w-9 h-9 bg-zinc-800 shrink-0">
                    {offer.product.images.length !== 0 && (
                      <AvatarImage
                        src={offer.product.images[0].url}
                        alt={`${offer.product.name} image`}
                      />
                    )}
                    <AvatarFallback className="bg-zinc-800">
                      {userInitials(offer.product.name)}
                    </AvatarFallback>
                  </Avatar>

                  {isCurrent && (
                    <Badge className="bg-primary/15 text-primary border-0 shrink-0">
                      {t("signatures:details.this_plan")}
                    </Badge>
                  )}
                </div>

                <article className="mt-3 min-w-0">
                  <p className="text-colorText font-medium text-sm line-clamp-2">
                    {offer.product.name}
                  </p>
                  {showOfferName && (
                    <p className="text-muted text-xs mt-0.5 line-clamp-1">
                      {offer.name}
                    </p>
                  )}
                  {metaLine && (
                    <p className="text-muted text-xs mt-1.5">{metaLine}</p>
                  )}
                </article>

                <Divider className="my-3" />

                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white text-base font-semibold">
                      {offer.price_formatted}
                    </p>
                    {showCurrencyNote && currencyNote && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex text-muted hover:text-colorText"
                            aria-label={currencyNote}
                          >
                            <Info size={14} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-[260px] text-xs leading-relaxed"
                        >
                          {currencyNote}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {hasSubscriptionLink && offer.subscription_amount_formatted && (
                    <p className="text-muted text-xs">
                      {t("signatures:details.next_cycle_amount", {
                        amount: offer.subscription_amount_formatted,
                      })}
                      {offer.subscription_next_billing_at_formatted
                        ? ` · ${offer.subscription_next_billing_at_formatted}`
                        : ""}
                    </p>
                  )}

                  {hasSubscriptionLink && !isCurrent && (
                    <Link
                      to={`/signatures/subscriptions-details/${offer.subscription_public_id}`}
                      className="inline-flex items-center gap-1 text-interactive text-xs font-medium hover:underline pt-1"
                    >
                      {t("sales:viewSubscription")}
                      <ExternalLink size={12} />
                    </Link>
                  )}
                </div>
              </Card>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </TooltipProvider>
  );
};

export default SaleSlider;
